import { z } from 'zod'
import { CountryCode } from '../constants/countries'
import { recurringSchema } from './recurring'

const optionalTrimmedStringSchema = z
  .string()
  .trim()
  .transform((value) => (value === '' ? null : value))
  .nullish()

const countryCodeValues = new Set<string>(Object.values(CountryCode))

export const optionalCountryCodeSchema = z
  .string()
  .trim()
  .transform((value) => (value === '' ? null : value.toUpperCase()))
  .refine((value) => value == null || countryCodeValues.has(value), {
    message: 'Must be a valid ISO 3166-1 alpha-2 country code',
  })
  .nullish()

export const listQueryZodFields = {
  sort: z.string().optional().nullable(),
  skip: z.coerce.number().min(0).optional().nullable(),
  limit: z.coerce.number().min(0).optional().nullable(),
  select: z.string().optional().nullable(),
}

export const queryBooleanSchema = z.preprocess((value) => {
  if (value === undefined || value === null || value === '') return undefined
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') {
    const lowered = value.toLowerCase()
    if (lowered === 'true') return true
    if (lowered === 'false') return false
  }
  return value
}, z.boolean())

export const listQueryOptionsSchema = z.object({
  ...listQueryZodFields,
})

export const addressSchema = z.object({
  /**
   * @deprecated - use number + street + apartment
   */
  number: z.string().optional().nullable(),
  street: z.string().optional().nullable(),
  address: z.string().optional().nullable(),
  address1: z.string().optional().nullable(),
  address2: z.string().optional().nullable(),
  //
  apartment: z.string().optional().nullable(),
  city: z.string().optional().nullable(),
  administrativeArea: z.string().optional().nullable(),
  administrativeAreaDisplay: z.string().optional().nullable(),
  /**
   * @deprecated - use administrativeArea
   */
  state: z.string().optional().nullable(),
  /**
   * ISO 3166-1 alpha-2 country code (e.g. `US`). Write paths validate this
   * with `addressInputSchema`.
   */
  country: z.string().optional().nullable(),
  postalCode: z.string().optional().nullable(),
  /**
   * @deprecated - use postalCode
   */
  zip: z.string().optional().nullable(),
  latitude: z.number().optional().nullable(),
  longitude: z.number().optional().nullable(),
})

export const addressInputSchema = z.object({
  /**
   * @deprecated - use number + street + apartment
   */
  number: optionalTrimmedStringSchema,
  street: optionalTrimmedStringSchema,
  address: optionalTrimmedStringSchema,
  address1: optionalTrimmedStringSchema,
  address2: optionalTrimmedStringSchema,
  apartment: optionalTrimmedStringSchema,
  city: optionalTrimmedStringSchema,
  administrativeArea: optionalTrimmedStringSchema,
  administrativeAreaDisplay: optionalTrimmedStringSchema,
  /**
   * @deprecated - use administrativeArea
   */
  state: optionalTrimmedStringSchema,
  country: optionalCountryCodeSchema,
  postalCode: optionalTrimmedStringSchema,
  /**
   * @deprecated - use postalCode
   */
  zip: optionalTrimmedStringSchema,
  latitude: z.number().min(-90).max(90).nullish(),
  longitude: z.number().min(-180).max(180).nullish(),
})

export const dbEntitySchema = z.object({
  _id: z.string(),
  id: z.string(),
  deleted: z.boolean(),
  created: z.date(),
  modified: z.date(),
})

export const submitterContextSchema = z.object({
  ipAddress: z.string().nullish(),
  userAgentBrowser: z.string().nullish(),
  userAgentOs: z.string().nullish(),
  prospectId: z.string().nullish(),
  prospectGuid: z.string().nullish(),
  managementUserId: z.string().nullish(),
  capturedAt: z.date().nullish(),
})

export const dbEntityRecurringSchema = dbEntitySchema.extend({
  recurring: recurringSchema.optional().nullable(),
  date: z.date(),
  venue: z.string(),
})
